import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, CATEGORY_META } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Clock, MapPin, Gavel, Calendar, User, ArrowLeft, MessageCircle, Share2, Play } from "lucide-react";
import { toast } from "sonner";
import { ShareSheet } from "@/components/ShareSheet";
import { VideoEmbed } from "@/components/VideoEmbed";
import { PayPalCheckout } from "@/components/PayPalCheckout";
import { useTranslation } from "react-i18next";
import { useGlobal } from "@/context/GlobalContext";
import { Money } from "@/components/Money";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { usd, fmtTime: fmtDate, timezone } = useGlobal();
  const [ev, setEv] = useState(null);
  const [bidAmount, setBidAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [fee, setFee] = useState(9.99);

  const load = async () => {
    try {
      const { data } = await api.get(`/events/${id}`);
      setEv(data);
    } catch {
      toast.error("Event not found");
      navigate("/feed");
    }
  };

  useEffect(() => {
    load();
    api.get("/payments/fee").then(({ data }) => setFee(data.processing_fee)).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  const buyTicket = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        event_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.toast_checkout_failed"));
      setBusy(false);
    }
  };

  const placeBid = async () => {
    const amt = parseFloat(bidAmount);
    if (!amt || amt <= 0) return toast.error("Enter a valid bid");
    setBusy(true);
    try {
      await api.post(`/events/${id}/bid`, { amount: amt });
      toast.success(t("common.toast_bid_placed", { amount: usd(amt) }));
      setBidAmount("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bid failed");
    } finally {
      setBusy(false);
    }
  };

  if (!ev) return <div className="min-h-screen bg-[#0B0F17]" />;

  const meta = CATEGORY_META[ev.category] || CATEGORY_META.other;
  const isAuction = ev.ticket_type === "auction";
  const isFree = ev.ticket_type === "free";
  const isHost = user?.id === ev.host_id;
  const isTopBidder = user?.id === ev.current_bidder_id;
  const currentBid = ev.current_bid || ev.starting_bid || 0;
  const nextMinBid = ev.current_bidder_id ? currentBid + (ev.bid_increment || 5) : ev.starting_bid || currentBid;
  const bidsCount = ev.bids?.length || 0;

  return (
    <div className="min-h-screen bg-[#0B0F17] pb-32">
      <TopBar />
      <div className="fixed top-14 left-0 right-0 z-30 bg-[#0B0F17]/85 backdrop-blur-md px-4 py-2 border-b border-white/5 flex items-center justify-between">
        <button onClick={() => navigate(-1)} data-testid="event-back" className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm">
          <ArrowLeft size={16} /> {t("event.back")}
        </button>
        <button onClick={() => setShareOpen(true)} data-testid="event-share-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs font-semibold hover:bg-white/5">
          <Share2 size={14} /> {t("event.share")}
        </button>
      </div>

      <main className="max-w-3xl mx-auto pt-24 px-4">
        <div className="relative rounded-2xl overflow-hidden aspect-[16/10] mb-5">
          <img src={ev.image_url || "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=1200&q=70"} className="w-full h-full object-cover" alt={ev.title} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-transparent to-transparent" />
          <div className="absolute top-4 left-4 flex gap-2">
            <span className={`overline px-2.5 py-1 rounded-full bg-${meta.color}-500/20 text-${meta.color}-300 border border-${meta.color}-500/30`}>
              {t(`categories.${ev.category}`, meta.label)}
            </span>
            {isAuction && (
              <span className="inline-flex items-center gap-1 overline px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 pulse-glow">
                <Gavel size={11} /> LIVE AUCTION
              </span>
            )}
            {ev.video_url && (
              <span className="inline-flex items-center gap-1 overline px-2.5 py-1 rounded-full bg-black/60 text-white border border-white/25">
                <Play size={11} fill="white" /> REEL
              </span>
            )}
          </div>
        </div>

        {ev.video_url && (
          <div className="mb-6" data-testid="event-video-section">
            <div className="overline text-slate-500 mb-2">WATCH THE CLIP</div>
            <VideoEmbed url={ev.video_url} />
          </div>
        )}

        <h1 data-testid="event-title" className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-tight mb-4">
          {ev.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 mb-6">
          <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {ev.city}{ev.venue ? ` · ${ev.venue}` : ""}</span>
          <span className="inline-flex items-center gap-1.5" data-testid="event-time"><Calendar size={14} /> {fmtDate(ev.starts_at)}</span>
          {ev.timezone && ev.timezone !== timezone && (
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-500" data-testid="event-venue-time">
              <Clock size={12} /> {t("event.venue_time")}: {fmtDate(ev.starts_at, ev.timezone, { weekday: undefined, month: undefined, day: undefined })}
            </span>
          )}
        </div>

        {ev.description && (
          <p className="text-slate-300 leading-relaxed mb-6 whitespace-pre-wrap">{ev.description}</p>
        )}

        {/* Host */}
        <div className="flex items-center gap-3 p-4 rounded-xl bg-[#131A26] border border-white/10 mb-6">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-violet-500 to-rose-500 flex items-center justify-center font-display font-bold">
            {ev.host_name?.[0]?.toUpperCase() || <User size={18} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-slate-500 overline">{t("event.hosted_by")}</div>
            <div className="font-semibold truncate">{ev.host_name}</div>
          </div>
          {!isHost && (
            <Link
              to={`/chat/${ev.host_id}`}
              data-testid="event-msg-host"
              className="px-3 py-2 rounded-lg border border-white/10 text-sm hover:bg-white/5 inline-flex items-center gap-1.5"
            >
              <MessageCircle size={14} /> {t("event.message")}
            </Link>
          )}
        </div>

        {/* Purchase / Bid card */}
        {ev.status === "sold" ? (
          <div className="p-5 rounded-2xl bg-emerald-500/10 border border-emerald-500/25 text-center">
            <div className="overline text-emerald-400 mb-1">{t("event.sold")}</div>
            <div className="font-display font-bold text-lg">{t("event.sold_msg")}</div>
          </div>
        ) : isFree ? (
          <div className="p-5 rounded-2xl bg-[#131A26] border border-white/10 text-center">
            <div className="overline text-emerald-400 mb-1">{t("event.free")}</div>
            <div className="text-slate-300 text-sm">{t("event.free_msg")}</div>
          </div>
        ) : isAuction ? (
          <div className="p-5 rounded-2xl bg-[#131A26] border border-amber-500/25">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="overline text-amber-400">{t("event.current_bid")}</div>
                <div className="font-display font-black text-4xl"><Money usd={currentBid} decimals={0} testid="event-current-bid" approxClassName="text-sm text-slate-400 font-mono font-normal" /></div>
                <div className="text-xs text-slate-500 mt-1">{bidsCount} {bidsCount === 1 ? t("event.bids") : t("event.bids_plural")} · {t("event.min_next")} {usd(nextMinBid)}</div>
              </div>
              {ev.auction_ends_at && (
                <div className="text-right">
                  <div className="overline text-slate-500">{t("event.ends")}</div>
                  <div className="font-mono text-sm text-amber-300">{fmtDate(ev.auction_ends_at)}</div>
                </div>
              )}
            </div>

            {isHost ? (
              <div className="text-sm text-slate-400 text-center py-2">{t("event.host_note")}</div>
            ) : isTopBidder ? (
              <>
                <Button onClick={buyTicket} disabled={busy} data-testid="event-checkout-auction" className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-xl">
                  {busy ? t("event.loading") : t("event.pay_with_card", { amount: usd(currentBid + fee) })}
                </Button>
                <div className="mt-3">
                  <PayPalCheckout eventId={ev.id} disabled={busy} />
                </div>
              </>
            ) : (
              <>
                <div className="flex gap-2 mb-3">
                  {[5, 10, 20].map((inc) => (
                    <button
                      key={inc}
                      onClick={() => setBidAmount((currentBid + inc).toFixed(2))}
                      data-testid={`bid-quick-${inc}`}
                      className="flex-1 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 font-mono font-bold hover:bg-amber-500/20 transition-colors"
                    >
                      +{usd(inc, 0)}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    data-testid="bid-input"
                    type="number"
                    step="0.01"
                    placeholder={`${nextMinBid.toFixed(2)}`}
                    value={bidAmount}
                    onChange={(e) => setBidAmount(e.target.value)}
                    className="flex-1 h-12 bg-[#0B0F17] border-white/10"
                  />
                  <Button onClick={placeBid} disabled={busy} data-testid="event-place-bid" className="h-12 px-5 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-xl">
                    {t("event.bid")}
                  </Button>
                </div>
              </>
            )}

            {ev.bids?.length > 0 && (
              <div className="mt-5 pt-5 border-t border-white/10">
                <div className="overline text-slate-500 mb-2">{t("event.recent_bids")}</div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {ev.bids.slice(0, 8).map((b) => (
                    <div key={b.id} className="flex justify-between text-sm">
                      <span className="text-slate-400">{b.user_name}</span>
                      <span className="font-mono font-bold text-amber-300">{usd(b.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-5 rounded-2xl bg-[#131A26] border border-white/10">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="overline text-rose-400">{t("event.ticket_price")}</div>
                <div className="font-display font-black text-4xl"><Money usd={ev.price || 0} decimals={0} testid="event-price" approxClassName="text-sm text-slate-400 font-mono font-normal" /></div>
                <div className="text-xs text-slate-500 mt-1">
                  {t("event.of_left", { left: ev.quantity - (ev.sold || 0), total: ev.quantity })}
                </div>
                <div className="text-[11px] text-slate-500 mt-1 font-mono">+ {usd(fee)} {t("event.processing_fee")}</div>
              </div>
              <Clock size={22} className="text-rose-400" />
            </div>
            {isHost ? (
              <div className="text-sm text-slate-400 text-center py-2">{t("event.this_is_yours")}</div>
            ) : (
              <>
                <Button onClick={buyTicket} disabled={busy} data-testid="event-buy-btn" className="w-full h-12 bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-xl">
                  {busy ? t("event.loading") : t("event.buy_with_card", { amount: usd((ev.price || 0) + fee) })}
                </Button>
                <div className="mt-3">
                  <PayPalCheckout eventId={ev.id} disabled={busy} />
                </div>
              </>
            )}
            {user?.credit_balance > 0 && !isHost && (
              <div className="mt-3 text-xs text-emerald-400 text-center" data-testid="event-credit-note">
                {t("event.credit_note", { amount: usd(user.credit_balance) })}
              </div>
            )}
          </div>
        )}
      </main>
      <ShareSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        url={`${window.location.origin}/api/share/${ev.id}`}
        text={`${ev.title} — on LastMinute E-Vite`}
        eventId={ev.id}
      />
    </div>
  );
}
